import { useEffect, useState } from "react";
import Form from "../../../Components/Form";
import FileInput from "../../../Components/FileInput/FileInput";
import DownloadOrderTemplate from "../../../Components/DownloadOrderTemplate";
import Button from "../../../Components/Button";
import useRequest from "../../../Hooks/useRequest";
import { toast } from "react-toastify";
import { CommonTableUI } from "../../../Tables/TableTemplate/common";
import {
  ErrorTableReport,
  FileOrderTableReport,
  ManualOrderRow,
} from "../../../Interfaces/orderInterfaces";
import {
  addManualBulkOrder,
  uploadManualOrderFile,
} from "../../../Requests/manualRequests";
import { Report } from "../../../Interfaces/TableInterfaces";
import SahraConnections from "../../Sahra/SahraConnections";
import FileBulkOrderTableUI from "../../../Tables/Order/FileBulkOrder/FileBulkOrderTableUI";
import Modal from "../../../Components/Modal/Modal";
import Loading from "../../../Components/Loading/Loading";
import PreviewVolumeSummaryTable from "../../../Tables/Order/PreviewVolumeSummaryTable";
import PreviewWarningsTable from "../../../Tables/Order/PreviewWarningsTable";
import ExcelStepOrderPreviewTable from "../../../Tables/Order/ExcelStepOrderPreviewTable";

enum ModalMode {
  BulkOrderPreview,
  BulkOrderUsers,
  BulkOrderResponseTable,
}

interface IProps {}

const BulkOrder = ({}: IProps) => {
  const [excelFile, setExcelFile] = useState<FormData | null>(null);
  const [bulkOrderRowsIds, setBulkOrderRowsIds] =
    useState<Array<ManualOrderRow> | null>(null);
  const [uploadFileErrorTableData, setUploadFileErrorTableData] =
    useState<Report<ErrorTableReport> | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState(ModalMode.BulkOrderPreview);

  function errorHandler(e: any) {
    if (e.response.status === 400) setUploadFileErrorTableData(e.response.data);
    if (e.response.status === 500) toast.error("خطا داخلی");
  }

  const addBulkOrderRequest = useRequest(
    () => addManualBulkOrder(bulkOrderRowsIds),
    (e) => e.response.data.value
  );

  const fileOrderRequest = useRequest(
    () => uploadManualOrderFile(excelFile),
    errorHandler
  );

  const bulkOrderClickHandler = () => {
    fileOrderRequest.fetchData();
  };

  const getOrderRows = (reports: Array<FileOrderTableReport>) => {
    setBulkOrderRowsIds(reports.map((report) => report.orderRow as ManualOrderRow));
  };

  useEffect(() => {
    if (fileOrderRequest.response) {
      setIsModalOpen(true);
      setModalMode(ModalMode.BulkOrderPreview);
      getOrderRows(fileOrderRequest.response.report.report);
      setUploadFileErrorTableData(null);
    }
  }, [fileOrderRequest.response]);

  const getBulkOrderPreviewTable = () => {
    if (uploadFileErrorTableData)
      return (
        <div className="flex flex-col justify-between items-center h-full">
          <div className="border-2 border-white border-b-red-700 text-red-700 w-full text-center mb-3">
            خطا
          </div>
          <CommonTableUI reports={uploadFileErrorTableData} />
        </div>
      );
    if (fileOrderRequest.response)
      return (
        <div className="flex flex-col justify-between items-center h-full gap-5">
          <PreviewVolumeSummaryTable values={fileOrderRequest.response.volumeSummary} />
          <ExcelStepOrderPreviewTable reports={fileOrderRequest.response.report} />
          <PreviewWarningsTable rows={fileOrderRequest.response.warnings} />
          <div className="mt-3 w-full">
            <Button
              onClick={() => setModalMode(ModalMode.BulkOrderUsers)}
              text="ارسال"
              variant="contained"
            />
          </div>
        </div>
      );
    else return <></>;
  };

  const getBulkOrderSahraConnectionsModalUI = () => {
    return (
      <div className="flex flex-col justify-start items-center gap-3">
        <SahraConnections initialSearchParam={{ userIds: getUserIds() }} />
        <Button
          onClick={bulkOrderSahraConnectionsModalBtnClickHandler}
          text="ارسال"
          variant="contained"
          loading={addBulkOrderRequest.loading}
        />
      </div>
    );
  };

  const getFileBulkOrderTableUI = () => {
    if (addBulkOrderRequest.response)
      return <FileBulkOrderTableUI reports={addBulkOrderRequest.response} />;
    return (
      <div className="flex justify-center items-center w-full h-60">
        <Loading className="!text-sky-500" />
      </div>
    );
  };

  const getModalUI = () => {
    switch (modalMode) {
      case ModalMode.BulkOrderPreview:
        return getBulkOrderPreviewTable();

      case ModalMode.BulkOrderUsers:
        return getBulkOrderSahraConnectionsModalUI();

      case ModalMode.BulkOrderResponseTable:
        return getFileBulkOrderTableUI();
    }
  };

  const getUserIds = () => {
    if (!bulkOrderRowsIds) return [];
    return bulkOrderRowsIds.map((row) => {
      return row.sahraUserId;
    });
  };

  const bulkOrderSahraConnectionsModalBtnClickHandler = () => {
    addBulkOrderRequest.fetchData();
    setModalMode(ModalMode.BulkOrderResponseTable);
  };

  const modalCloseHandler = () => {
    switch (modalMode) {
      case ModalMode.BulkOrderPreview: {
        setIsModalOpen(false);
        break;
      }
      case ModalMode.BulkOrderUsers: {
        setModalMode(ModalMode.BulkOrderPreview);
        break;
      }
      case ModalMode.BulkOrderResponseTable: {
        setIsModalOpen(false);
        break;
      }

      default:
        break;
    }
  };

  return (
    <Form>
      {isModalOpen ? (
        <Modal title="ارسال دسته‌ای" onClose={modalCloseHandler} UI={getModalUI()} />
      ) : (
        <></>
      )}
      <div className="p-2 w-full">
        <div className="bold mb-2 text-xs">ارسال دسته ای</div>
        <div className="flex flex-wrap items-center gap-3">
          <FileInput
            fileExtension=".xlsx, .xls, .csv"
            UI={<></>}
            onChange={setExcelFile}
            value={excelFile}
            name="formFile"
            label="انتخاب فایل اکسل"
          />
          <DownloadOrderTemplate kind="manual" />
        </div>
        <div className="mt-3 w-full">
          <Button
            text="ارسال"
            onClick={bulkOrderClickHandler}
            variant="contained"
            disabled={!excelFile}
            loading={fileOrderRequest.loading}
          />
        </div>
      </div>
    </Form>
  );
};

export default BulkOrder;
